import React from 'react'
import SwipeableViews from 'react-swipeable-views'
import { bindKeyboard } from 'react-swipeable-views-utils'
import { Link, navigate } from 'gatsby'
import styled from 'styled-components'
import { Flex, Box } from 'rebass'
import media, {
  LargeScreenUp,
  SmallToMediumScreenOnly,
  CategoryBadge
} from '../../utils/style'
import { Heading } from 'rebass'
import { array } from 'prop-types'
import buildImageUrl from '../../../lib/media-url-maker'
import moment from 'moment'

const BindKeyboardSwipeableViews = bindKeyboard(SwipeableViews)

/*************************
 STYLES: WRAPPER
 **************************/

const ContentFrame = styled(Box)`
  width: 100%;
  background-color: ${(props) => props.bg};
  max-width: ${(props) => props.theme.content.max.width};
  position: relative;
  z-index: 1;
`

const ContentFrameWithShadow = styled(ContentFrame)`
  overflow: hidden;
  ${media.LargeScreenUp`
    overflow: visible;
    &::before {
      content:'';
      position: absolute;
      z-index: 0;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 95%;
      height: 100%;
      box-shadow:
        0 25px 25px 0px rgba(66,76,94,0.05),
        0 20px 20px 0 rgba(0,0,0,0.01);
    }
  `};
  > div > div {
    transition: transform 0.4s ${(props) => props.theme.ease.ease_out_back};
  }
  .article_preview__nudge-left {
    transform: translateX(0.8%);
  }
  .article_preview__nudge-right {
    transform: translateX(-0.8%);
  }
`

/*************************
 STYLES: POSTS
 **************************/

const ArticlePreviewWrapper = styled(Flex)`
  position: relative;
  cursor: pointer;
  z-index: 1;
  background-color: white;
  border-right: 1px ${(props) => props.theme.colors.grey_op10} solid;
  transition: background-color 0.4s ${(props) => props.theme.ease.ease_out_expo};

  &:hover {
    ${media.LargeScreenUp`
      background-color: ${(props) => props.theme.colors.blue_op20};
    `}
    a {
      color: ${(props) => props.theme.colors.blue};
    }
    path {
      fill: ${(props) => props.theme.colors.blue};
    }
  }

  a {
    color: ${(props) => props.theme.colors.grey};
    text-decoration: none;
    &:hover {
      color: ${(props) => props.theme.colors.blue};
    }
  }
`

const ArticlePreviewWrapperWithBg = styled(ArticlePreviewWrapper)`
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top center;
  background-image: url(${(props) => props.bg});

  @media only screen and (-webkit-min-device-pixel-ratio: 1.3),
    only screen and (-o-min-device-pixel-ratio: 1.3/1),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-image: url('${(props) => props.bg}');
  }
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${(props) => props.theme.colors.grey};
    opacity: 0.5;
    transition: opacity 0.4s ${(props) => props.theme.ease.ease_out_expo};
  }

  a,
  time {
    color: white;
  }

  &:hover {
    a,
    time {
      color: white;
    }
  }

  &:hover::before {
    opacity: 0.35;
  }
`

const ArticlePreviewBox = styled(Box)`
  position: relative;
  min-height: 235px;
  max-width: 75%;
`

const DateTime = styled.time`
  font-size: 14px;
  color: ${(props) => props.theme.colors.grey_light};
  font-weight: ${(props) => props.theme.weights[1]};
  position: absolute;
  left: 30px;
  bottom: 20px;
  ${media.LargeScreenUp`
    left: 40px;
    bottom: 32px;
  `};
`

const TransitionNext = styled.div`
  cursor: pointer;
  width: 10%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  svg {
    height: 100%;
  }
`

const TransitionPrev = styled.div`
  cursor: pointer;
  position: absolute;
  width: 10%;
  height: 100%;
  top: 0;
  left: 0;
  svg {
    height: 100%;
  }
`

const addFullUrlPath = (article) => ({
  ...article,
  fullUrlPath: `/blog/${article.slug}`
})

class ContentArticleCards extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = { index: 0 }
    this._next = this._next.bind(this)
    this._prev = this._prev.bind(this)
    this._nudgeLeft = this._nudgeLeft.bind(this)
    this._nudgeRight = this._nudgeRight.bind(this)
    this._nudgeDefault = this._nudgeDefault.bind(this)
    this._handleChangeIndex = this._handleChangeIndex.bind(this)
  }

  _handleChangeIndex(index) {
    this.setState({
      index,
      nudge: null
    })
  }

  _nudgeLeft(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: 'article_preview__nudge-left'
    })
  }

  _nudgeRight(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: 'article_preview__nudge-right'
    })
  }

  _nudgeDefault(_e) {
    if (_e) _e.preventDefault()
    this.setState({
      nudge: null
    })
  }

  _next(_e) {
    if (_e) _e.preventDefault()
    this._handleChangeIndex(this.state.index + 1)
  }

  _prev(_e) {
    if (_e) _e.preventDefault()
    this._handleChangeIndex(this.state.index - 1)
  }

  render() {
    const max = this.props.max || 3
    const articles = this.props.articles.slice(0, max)
    const { index } = this.state
    const styles = {
      root: {
        padding: '0 10%',
        width: '100%',
        overflow: 'visible'
      },
      slideContainer: {
        width: '100%'
      }
    }

    const articlePreviews = articles
      .filter((article) => article.shortHeadline.length > 0)
      .map(addFullUrlPath)
      .map((article, index) => {
        const imageUrl =
          article.images.length > 0 &&
          buildImageUrl({
            width: 460,
            height: 260,
            mode: 'cover',
            resource: article.images[0]
          })
        const ArticlePreviewWrapperFromIndex =
          index === 0 ? ArticlePreviewWrapperWithBg : ArticlePreviewWrapper
        return (
          <ArticlePreviewWrapperFromIndex
            width={[null, null, 4 / 12]}
            key={article.id}
            onClick={() => navigate(article.fullUrlPath)}
            bg={imageUrl}
            index={index}
            className={'transition-preview'}
          >
            <ArticlePreviewBox
              pl={[30, null, 40]}
              pr={[30, null, 40]}
              pt={[25, 30]}
              pb={[100]}
            >
              {article.tags.length > 0 && (
                <CategoryBadge ml={'0px'} mb={10}>
                  {article.tags[0]}
                </CategoryBadge>
              )}
              <Heading fontSize={[3, 5]} mt={[2]}>
                <Link to={article.fullUrlPath}>{article.shortHeadline}</Link>
              </Heading>
              <DateTime dateTime={article.displayDate}>
                {moment(article.displayDate).format('dddd, D MMMM YYYY')}
              </DateTime>
            </ArticlePreviewBox>
          </ArticlePreviewWrapperFromIndex>
        )
      })

    return (
      <ContentFrameWithShadow
        mt={[0, 0, 0, 10]}
        mb={[80]}
        bg="white"
        as="article"
      >
        <LargeScreenUp>
          <Flex>{articlePreviews}</Flex>
        </LargeScreenUp>
        <SmallToMediumScreenOnly>
          <BindKeyboardSwipeableViews
            enableMouseEvents
            index={index}
            onChangeIndex={this._handleChangeIndex}
            style={styles.root}
            slideStyle={styles.slideContainer}
            className={this.state.nudge}
          >
            {articlePreviews}
          </BindKeyboardSwipeableViews>
          {this.state.index < articles.length - 1 && (
            <TransitionNext
              id="transition-next"
              onClick={this._next}
              onMouseEnter={this._nudgeRight}
              onMouseOut={this._nudgeDefault}
            />
          )}
          {this.state.index > 0 && (
            <TransitionPrev
              id="transition-prev"
              onClick={this._prev}
              onMouseEnter={this._nudgeLeft}
              onMouseOut={this._nudgeDefault}
            />
          )}
        </SmallToMediumScreenOnly>
      </ContentFrameWithShadow>
    )
  }
}

ContentArticleCards.defaultProps = {
  articles: []
}
ContentArticleCards.propTypes = {
  articles: array
}

export default ContentArticleCards
